import { useEffect, useState, useRef } from 'react';
import {
  listSchools,
  createSchool,
  editSchool,
  deleteSchool,
} from '@dataconnect/generated';
import Constants from '../../utilities/constants/Constants';
import { FaUpload, FaFileCsv, FaInfoCircle } from 'react-icons/fa';

const Schools = () => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [success, setSuccess] = useState(null);

  const [adding, setAdding] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [form, setForm] = useState({ 
    name: '', isActive: true, subjects: [],
    affiliationNumber: '', schoolCode: '', status: '', state: '', district: '',
    address: '', website: ''
  });

  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ 
    name: '', isActive: true, subjects: [],
    affiliationNumber: '', schoolCode: '', status: '', state: '', district: '',
    address: '', website: ''
  });

  const [creating, setCreating] = useState(false);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkSyncing, setBulkSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [districtFilter, setDistrictFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const fileInputRef = useRef(null);

  async function refresh() {
    setErr(null);
    setLoading(true);
    try {
      // Use the first word for server-side filtering to stay within the 100 limit
      // but still be flexible enough for client-side fuzzy matching.
      const searchWords = searchTerm?.toLowerCase().split(/[^a-z0-9]/i).filter(Boolean) || [];
      const serverSearch = searchWords[0] || undefined;
      
      const res = await listSchools({ 
        limit: serverSearch ? 500 : 100, // Fetch more if searching to improve fuzzy matching
        search: serverSearch,
        state: stateFilter || undefined,
        district: districtFilter || undefined
      });
      const data = res.data?.schools;
      setSchools(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e.message || 'Failed to load schools');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      refresh();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, stateFilter, districtFilter]);

  // ---- bulk upload ----
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      const rows = text.split('\n').filter(row => row.trim());
      if (rows.length < 2) {
        setErr('CSV file is empty or missing data rows');
        return;
      }

      setBulkProcessing(true);
      setErr(null);
      setSuccess(null);

      // Parse headers
      const headers = rows[0].split(',').map(h => h.trim().toLowerCase());
      const dataRows = rows.slice(1);
      const totalRows = dataRows.length;
      
      setUploadProgress({ current: 0, total: totalRows });
      
      let successCount = 0;
      let failCount = 0;
      let updateCount = 0;

      // Get existing schools to enable "Upsert" behavior
      const existingSchoolsRes = await listSchools({ limit: 5000 });
      const existingSchoolsMap = new Map(
        (existingSchoolsRes.data?.schools || [])
          .map(s => [s.affiliationNumber?.trim().toLowerCase(), s.id ?? s._id ?? s.schoolId])
          .filter(([aff]) => Boolean(aff))
      );

      // Process in chunks to avoid overwhelming the browser/server
      const CHUNK_SIZE = 20;
      for (let i = 0; i < dataRows.length; i += CHUNK_SIZE) {
        const chunk = dataRows.slice(i, i + CHUNK_SIZE);
        
        const chunkPromises = chunk.map(async (row) => {
          // Simple CSV split (doesn't handle commas in quotes, but usually enough for simple data)
          const values = row.split(',').map(v => v.trim());
          const entry = {};
          headers.forEach((h, i) => { entry[h] = values[i]; });

          // Map CSV headers to internal fields
          const schoolData = {
            name: entry['name'] || entry['school name'],
            state: entry['state'],
            district: entry['district'],
            affiliationNumber: entry['affiliation number'] || entry['aff no'],
            schoolCode: entry['school code'],
            status: entry['status'],
            address: entry['address'],
            website: entry['website'],
            isActive: entry['active']?.toLowerCase() === 'true' || true,
            subjects: ['KANNADA'] // Default subject
          };

          // Generate normalized search field
          schoolData.searchField = [
            schoolData.name,
            schoolData.address,
            schoolData.affiliationNumber,
            schoolData.schoolCode,
            schoolData.district,
            schoolData.state
          ].filter(Boolean).join(' ').replace(/[^a-z0-9]/gi, ' ').replace(/\s+/g, ' ').trim().toLowerCase();

          if (!schoolData.name) {
            return { success: false, row, reason: 'Missing Name' };
          }

          // Check for existing school by affiliation number
          const existingId = schoolData.affiliationNumber 
            ? existingSchoolsMap.get(schoolData.affiliationNumber.trim().toLowerCase()) 
            : null;

          try {
            if (existingId) {
              // Update existing record (Fixes search index and other fields)
              await editSchool({ id: existingId, ...schoolData });
              return { success: true, updated: true };
            } else {
              // Create new record
              await createSchool(schoolData);
              return { success: true };
            }
          } catch (error) {
            console.error('Bulk upload error for row:', row, error);
            return { success: false, row };
          }
        });

        const results = await Promise.all(chunkPromises);
        
        results.forEach(res => {
          if (res.success) {
            if (res.updated) updateCount++;
            else successCount++;
          } else {
            failCount++;
          }
        });

        const currentProcessed = Math.min(i + CHUNK_SIZE, totalRows);
        setUploadProgress({ current: currentProcessed, total: totalRows });
      }

      setSuccess(`Upload Complete: ${successCount} created, ${updateCount} updated. ${failCount > 0 ? `${failCount} failed.` : ''}`);
      setShowBulkUpload(false);
      setUploadProgress({ current: 0, total: 0 });
      await refresh();
      setBulkProcessing(false);
    };
    reader.readAsText(file);
  };

  // ---- add ----
  async function handleAddSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) return;
    setCreating(true);
    setErr(null);

    try {
      const searchField = [
        form.name,
        form.address,
        form.affiliationNumber,
        form.schoolCode,
        form.district,
        form.state
      ].filter(Boolean).join(' ').replace(/[^a-z0-9]/gi, ' ').replace(/\s+/g, ' ').trim().toLowerCase();

      await createSchool({ 
        name: form.name, 
        isActive: form.isActive, 
        subjects: form.subjects,
        affiliationNumber: form.affiliationNumber,
        schoolCode: form.schoolCode,
        status: form.status,
        state: form.state,
        district: form.district,
        address: form.address,
        website: form.website,
        searchField: searchField
      });
      await refresh();
      setForm({ 
        name: '', isActive: true, subjects: [], affiliationNumber: '', schoolCode: '', 
        status: '', state: '', district: '', address: '', website: '' 
      });
      setAdding(false);
    } catch (e) {
      setErr(e.message || 'Failed to create');
    } finally {
      setCreating(false);
    }
  }

  const toggleSubject = (value, isAddOperation) => {
    if (isAddOperation) {
      setForm(f => {
        const set = new Set(f.subjects || []);
        set.has(value) ? set.delete(value) : set.add(value);
        return { ...f, subjects: Array.from(set) };
      });
    } else {
      setEditForm(f => {
        const set = new Set(f.subjects || []);
        set.has(value) ? set.delete(value) : set.add(value);
        return { ...f, subjects: Array.from(set) };
      });
    }
  };

  // ---- edit ----
  function startEdit(s) {
    setEditId(s.id ?? s._id ?? s.schoolId); // pick your key field
    setEditForm({
      name: s.name ?? '',
      isActive: !!s.isActive,
      subjects: s.subjects ?? [],
      affiliationNumber: s.affiliationNumber ?? '',
      schoolCode: s.schoolCode ?? '',
      status: s.status ?? '',
      state: s.state ?? '',
      district: s.district ?? '',
      address: s.address ?? '',
      website: s.website ?? ''
    });
  }

  async function saveEdit(id) {
    if (!editForm.name.trim()) return;
    setSavingId(id);
    setErr(null);
    try {
      const searchField = [
        editForm.name,
        editForm.address,
        editForm.affiliationNumber,
        editForm.schoolCode,
        editForm.district,
        editForm.state
      ].filter(Boolean).join(' ').replace(/[^a-z0-9]/gi, ' ').replace(/\s+/g, ' ').trim().toLowerCase();

      await editSchool({ id, ...editForm, searchField });
      await refresh();
      setEditId(null);
    } catch (e) {
      setErr(e.message || 'Failed to update');
    } finally {
      setSavingId(null);
    }
  }

  function cancelEdit() {
    setEditId(null);
  }

  // ---- delete ----
  async function removeSchool(id) {
    if (!window.confirm('Delete this school?')) return;
    setDeletingId(id);
    setErr(null);
    try {
      await deleteSchool({ id });
      await refresh();
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (e) {
      setErr(e.message || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  }

  async function handleBulkDelete() {
    const count = selectedIds.size;
    if (count === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${count} selected schools?`)) return;

    setBulkDeleting(true);
    setErr(null);
    setSuccess(null);
    setUploadProgress({ current: 0, total: count });

    const idsArray = Array.from(selectedIds);
    let successCount = 0;
    let failCount = 0;

    // Process in chunks
    const CHUNK_SIZE = 20;
    for (let i = 0; i < idsArray.length; i += CHUNK_SIZE) {
      const chunk = idsArray.slice(i, i + CHUNK_SIZE);
      const chunkPromises = chunk.map(async (id) => {
        try {
          await deleteSchool({ id });
          return { success: true };
        } catch (error) {
          console.error('Bulk delete error for ID:', id, error);
          return { success: false };
        }
      });

      const results = await Promise.all(chunkPromises);
      results.forEach(res => {
        if (res.success) successCount++;
        else failCount++;
      });

      const currentProcessed = Math.min(i + CHUNK_SIZE, count);
      setUploadProgress({ current: currentProcessed, total: count });
    }

    setSuccess(`Successfully deleted ${successCount} schools. ${failCount > 0 ? `${failCount} failed.` : ''}`);
    setSelectedIds(new Set());
    setUploadProgress({ current: 0, total: 0 });
    await refresh();
    setBulkDeleting(false);
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === schools.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(schools.map(s => s.id ?? s._id ?? s.schoolId)));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filteredSchools = schools.filter(s => {
    if (!searchTerm) return true;
    const searchWords = searchTerm.toLowerCase().split(/[^a-z0-9]/i).filter(Boolean);
    const searchableText = (s.searchField || '').toLowerCase();
    return searchWords.every(word => searchableText.includes(word));
  });

  // To keep filters accurate across all 1800+ schools, we'd normally need a separate query
  const districts = Array.from(new Set(schools.map(s => s.district).filter(Boolean))).sort();
  const states = Array.from(new Set(schools.map(s => s.state).filter(Boolean))).sort();



  async function handleBulkSync() {
    if (!window.confirm('This will update the search index for all 100 visible schools. Proceed?')) return;
    
    setBulkSyncing(true);
    setErr(null);
    setSuccess(null);
    setUploadProgress({ current: 0, total: schools.length });

    let successCount = 0;
    let failCount = 0;

    const CHUNK_SIZE = 20;
    for (let i = 0; i < schools.length; i += CHUNK_SIZE) {
      const chunk = schools.slice(i, i + CHUNK_SIZE);
      const chunkPromises = chunk.map(async (s) => {
        const id = s.id ?? s._id ?? s.schoolId;
        const searchField = [
          s.name,
          s.address,
          s.affiliationNumber,
          s.schoolCode,
          s.district,
          s.state
        ].filter(Boolean).join(' ').replace(/[^a-z0-9]/gi, ' ').replace(/\s+/g, ' ').trim().toLowerCase();

        try {
          await editSchool({ 
            id, 
            name: s.name,
            isActive: s.isActive,
            subjects: s.subjects,
            affiliationNumber: s.affiliationNumber,
            schoolCode: s.schoolCode,
            status: s.status,
            state: s.state,
            district: s.district,
            address: s.address,
            website: s.website,
            searchField 
          });
          return { success: true };
        } catch (error) {
          console.error('Sync error for ID:', id, error);
          return { success: false };
        }
      });

      const results = await Promise.all(chunkPromises);
      results.forEach(res => {
        if (res.success) successCount++;
        else failCount++;
      });

      const currentProcessed = Math.min(i + CHUNK_SIZE, schools.length);
      setUploadProgress({ current: currentProcessed, total: schools.length });
    }

    setSuccess(`Successfully synced search index for ${successCount} schools.`);
    setUploadProgress({ current: 0, total: 0 });
    await refresh();
    setBulkSyncing(false);
  }

  // ---- UI ----
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-3">
          <button
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:cursor-not-allowed flex items-center gap-2"
            onClick={() => { setAdding(a => !a); setShowBulkUpload(false); }}
            type="button"
            disabled={loading || bulkProcessing || bulkDeleting}
          >
            {adding ? 'Close' : 'Add School'}
          </button>
          <button
            className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:cursor-not-allowed flex items-center gap-2"
            onClick={() => { setShowBulkUpload(b => !b); setAdding(false); }}
            type="button"
            disabled={loading || bulkProcessing || bulkDeleting || bulkSyncing}
          >
            <FaUpload /> {showBulkUpload ? 'Close' : 'Bulk Upload'}
          </button>
          <button
            className="bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:cursor-not-allowed flex items-center gap-2"
            onClick={handleBulkSync}
            type="button"
            title="Fix search index for currently visible schools"
            disabled={loading || bulkProcessing || bulkDeleting || bulkSyncing || schools.length === 0}
          >
            {bulkSyncing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Syncing ({uploadProgress.current}/{uploadProgress.total})...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Sync Search
              </>
            )}
          </button>
          {selectedIds.size > 0 && (
            <button
              className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:cursor-not-allowed flex items-center gap-2"
              onClick={handleBulkDelete}
              type="button"
              disabled={loading || bulkProcessing || bulkDeleting}
            >
              {bulkDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Deleting ({uploadProgress.current}/{uploadProgress.total})...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete Selected ({selectedIds.size})
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 border border-gray-200 rounded-lg mb-6 flex flex-wrap gap-4 items-center">
        <div className="flex-1 min-w-[200px] relative">
          <svg className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search by name, aff no, code, or address..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="flex gap-2 items-center">
          <span className="text-sm font-medium text-gray-600">State:</span>
          <select 
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            value={stateFilter}
            onChange={e => setStateFilter(e.target.value)}
          >
            <option value="">All States</option>
            {states.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div className="flex gap-2 items-center">
          <span className="text-sm font-medium text-gray-600">District:</span>
          <select 
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            value={districtFilter}
            onChange={e => setDistrictFilter(e.target.value)}
          >
            <option value="">All Districts</option>
            {districts.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>

        {(searchTerm || stateFilter || districtFilter) && (
          <button 
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            onClick={() => { setSearchTerm(''); setStateFilter(''); setDistrictFilter(''); }}
          >
            Reset Filters
          </button>
        )}
        
        <div className="ml-auto text-sm text-gray-500 font-medium">
          Showing top {schools.length} results
        </div>
      </div>

      {err && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">{err}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">{success}</div>}
      
      <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FaInfoCircle className="text-amber-500" />
          <p className="text-sm">
            <strong>Search Tip:</strong> If some schools are not appearing in search results, please click <strong>Sync Search</strong> to update the index.
          </p>
        </div>
      </div>

      {showBulkUpload && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <span className="bg-emerald-100 text-emerald-600 p-2 rounded-lg">
                <FaFileCsv className="w-5 h-5" />
              </span>
              Bulk Upload Schools
            </h3>
            <p className="text-sm text-gray-600 mt-1">Upload a CSV file with school details</p>
          </div>
          <div className="p-6">
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-4 flex gap-3 items-start">
              <FaInfoCircle className="text-blue-500 mt-1 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">CSV Format Requirements:</p>
                <p>Headers should include: <code className="bg-blue-100 px-1 rounded">Name</code>, <code className="bg-blue-100 px-1 rounded">State</code>, <code className="bg-blue-100 px-1 rounded">District</code>, <code className="bg-blue-100 px-1 rounded">Aff No</code>, <code className="bg-blue-100 px-1 rounded">School Code</code>, <code className="bg-blue-100 px-1 rounded">Status</code>, <code className="bg-blue-100 px-1 rounded">Address</code>, <code className="bg-blue-100 px-1 rounded">Website</code></p>
              </div>
            </div>
            
            <input
              type="file"
              accept=".csv"
              ref={fileInputRef}
              onChange={handleFileUpload}
              className="hidden"
            />
            
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={bulkProcessing}
              className="w-full py-8 border-2 border-dashed border-gray-300 rounded-xl hover:border-emerald-500 hover:bg-emerald-50 transition-all flex flex-col items-center justify-center gap-3 disabled:cursor-not-allowed"
            >
              {bulkProcessing ? (
                <>
                  <div className="w-64 h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
                    <div 
                      className="h-full bg-emerald-500 transition-all duration-300"
                      style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-gray-600 font-medium">
                    Uploading schools: {uploadProgress.current} / {uploadProgress.total}
                  </span>
                  <span className="text-xs text-gray-400 italic">Please do not close this window</span>
                </>
              ) : (
                <>
                  <FaFileCsv className="text-4xl text-gray-400" />
                  <span className="text-gray-600 font-medium">Click to select CSV file</span>
                  <span className="text-xs text-gray-400">or drag and drop here</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {adding && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <span className="bg-emerald-100 text-emerald-600 p-2 rounded-lg">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </span>
              Add New School
            </h3>
            <p className="text-sm text-gray-600 mt-1">Register a new school with subjects and settings</p>
          </div>
          <form className="p-6" onSubmit={handleAddSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {/* School Name */}
              <div className="md:col-span-2 lg:col-span-2">
                <label htmlFor="add-name" className="block text-sm font-medium text-gray-700 mb-1">
                  School Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="add-name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="School name..."
                  required
                  disabled={creating}
                />
              </div>

              {/* District */}
              <div>
                <label htmlFor="add-district" className="block text-sm font-medium text-gray-700 mb-1">
                  District
                </label>
                <input
                  id="add-district"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                  value={form.district}
                  onChange={e => setForm(f => ({ ...f, district: e.target.value }))}
                  placeholder="District..."
                  disabled={creating}
                />
              </div>

              {/* State */}
              <div>
                <label htmlFor="add-state" className="block text-sm font-medium text-gray-700 mb-1">
                  State
                </label>
                <input
                  id="add-state"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                  value={form.state}
                  onChange={e => setForm(f => ({ ...f, state: e.target.value }))}
                  placeholder="State..."
                  disabled={creating}
                />
              </div>

              {/* Aff No */}
              <div>
                <label htmlFor="add-aff" className="block text-sm font-medium text-gray-700 mb-1">
                  Affiliation Number
                </label>
                <input
                  id="add-aff"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                  value={form.affiliationNumber}
                  onChange={e => setForm(f => ({ ...f, affiliationNumber: e.target.value }))}
                  placeholder="Affiliation No..."
                  disabled={creating}
                />
              </div>

              {/* School Code */}
              <div>
                <label htmlFor="add-code" className="block text-sm font-medium text-gray-700 mb-1">
                  School Code
                </label>
                <input
                  id="add-code"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                  value={form.schoolCode}
                  onChange={e => setForm(f => ({ ...f, schoolCode: e.target.value }))}
                  placeholder="School code..."
                  disabled={creating}
                />
              </div>

              {/* Status Text */}
              <div>
                <label htmlFor="add-status" className="block text-sm font-medium text-gray-700 mb-1">
                  Status Text
                </label>
                <input
                  id="add-status"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                  value={form.status}
                  onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                  placeholder="Status (e.g. Active, Closed)..."
                  disabled={creating}
                />
              </div>

              {/* Website */}
              <div className="md:col-span-2 lg:col-span-2">
                <label htmlFor="add-website" className="block text-sm font-medium text-gray-700 mb-1">
                  Website
                </label>
                <input
                  id="add-website"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                  value={form.website}
                  onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
                  placeholder="https://www.example.com"
                  disabled={creating}
                />
              </div>

              {/* Address */}
              <div className="md:col-span-2 lg:col-span-3">
                <label htmlFor="add-address" className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <textarea
                  id="add-address"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                  value={form.address}
                  onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                  placeholder="Full school address..."
                  disabled={creating}
                />
              </div>

              {/* Active Checkbox */}
              <div className="flex items-center h-[38px]">
                <input
                  id="add-active"
                  className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                  type="checkbox"
                  checked={form.isActive}
                  onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
                  disabled={creating}
                />
                <label className="ml-2 text-sm text-gray-700" htmlFor="add-active">
                  Active in App
                </label>
              </div>
            </div>

            {/* Subjects Selection */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subjects
              </label>
              <div className="flex flex-wrap gap-3 p-4 border border-gray-300 rounded-md bg-gray-50 min-h-[38px]">
                {Constants.SUBJECT_OPTIONS.map((opt, idx) => {
                  const id = `subject-${idx}`;
                  const checked = form.subjects?.includes(opt.value) || false;
                  return (
                    <label key={opt.value} className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-all ${
                      checked 
                        ? 'bg-emerald-100 text-emerald-800 border-2 border-emerald-500' 
                        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
                    }`}>
                      <input
                        className="sr-only"
                        type="checkbox"
                        id={id}
                        checked={checked}
                        onChange={() => toggleSubject(opt.value, true)}
                        disabled={creating}
                      />
                      {opt.label}
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 border-t border-gray-100 pt-6">
              <button
                className="border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-2 px-6 rounded-md transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                type="button"
                onClick={() => setForm({ 
                  name: '', isActive: true, subjects: [], affiliationNumber: '', schoolCode: '', 
                  status: '', state: '', district: '', address: '', website: '' 
                })}
                disabled={creating}
              >
                Clear
              </button>
              <button 
                className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white font-medium py-2 px-8 rounded-md transition-colors disabled:cursor-not-allowed flex items-center gap-2" 
                type="submit" 
                disabled={creating}
              >
                {creating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Save School
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}



      {loading ? (
        <div className="flex items-center gap-2 text-gray-500">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
          Loading schools…
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={schools.length > 0 && selectedIds.size === schools.length}
                    onChange={toggleSelectAll}
                    disabled={loading || bulkDeleting}
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">Name / Info</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[150px]">District / State / Address</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">Aff No / Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[100px]">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[220px]">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSchools.map(school => {
                const id = school.id ?? school._id ?? school.schoolId; // normalize id field
                const isEditing = editId === id;
                const isSelected = selectedIds.has(id);
                return (
                  <tr key={id} className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50/50' : ''}`}>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        checked={isSelected}
                        onChange={() => toggleSelect(id)}
                        disabled={loading || bulkDeleting}
                      />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {isEditing ? (
                        <div className="space-y-2">
                          <input
                            className="w-full px-3 py-1 border border-gray-300 rounded-md text-sm font-bold"
                            value={editForm.name}
                            onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                            placeholder="Name"
                            required
                          />
                          <input
                            className="w-full px-3 py-1 border border-gray-300 rounded-md text-xs"
                            value={editForm.website}
                            onChange={e => setEditForm(f => ({ ...f, website: e.target.value }))}
                            placeholder="Website URL"
                          />
                          <div className="flex flex-wrap gap-2">
                            {Constants.SUBJECT_OPTIONS.map((opt, idx) => {
                              const checkId = `subject-edit-${id}-${idx}`;
                              const checked = !!editForm.subjects?.includes(opt.value);
                              return (
                                <label key={opt.value} className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold cursor-pointer border ${checked ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white border-gray-200 text-gray-500'}`}>
                                  <input type="checkbox" className="sr-only" checked={checked} onChange={() => toggleSubject(opt.value, false)} />
                                  {opt.label}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="font-bold text-gray-900">{school.name}</div>
                          {school.website && (
                            <a href={school.website} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline block truncate max-w-[200px]">
                              {school.website.replace(/^https?:\/\/(www\.)?/, '')}
                            </a>
                          )}
                          <div className="flex flex-wrap gap-1 mt-1">
                            {Array.isArray(school.subjects) && school.subjects.map(s => (
                              <span key={s} className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-[10px] font-bold border border-blue-100 uppercase">{s}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {isEditing ? (
                        <div className="space-y-1">
                          <input
                            className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                            value={editForm.district}
                            onChange={e => setEditForm(f => ({ ...f, district: e.target.value }))}
                            placeholder="District"
                          />
                          <input
                            className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                            value={editForm.state}
                            onChange={e => setEditForm(f => ({ ...f, state: e.target.value }))}
                            placeholder="State"
                          />
                          <textarea
                            className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                            rows={2}
                            value={editForm.address}
                            onChange={e => setEditForm(f => ({ ...f, address: e.target.value }))}
                            placeholder="Address"
                          />
                        </div>
                      ) : (
                        <div className="text-xs space-y-0.5">
                          <div className="font-medium text-gray-700">{school.district}{school.district && school.state ? ', ' : ''}{school.state}</div>
                          {school.address && <div className="text-gray-400 italic line-clamp-2 max-w-[150px]">{school.address}</div>}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {isEditing ? (
                        <div className="space-y-1">
                          <input
                            className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                            value={editForm.affiliationNumber}
                            onChange={e => setEditForm(f => ({ ...f, affiliationNumber: e.target.value }))}
                            placeholder="Aff No"
                          />
                          <input
                            className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                            value={editForm.schoolCode}
                            onChange={e => setEditForm(f => ({ ...f, schoolCode: e.target.value }))}
                            placeholder="Code"
                          />
                        </div>
                      ) : (
                        <div className="text-xs space-y-0.5 font-mono">
                          {school.affiliationNumber && <div><span className="text-gray-400">Aff:</span> {school.affiliationNumber}</div>}
                          {school.schoolCode && <div><span className="text-gray-400">Code:</span> {school.schoolCode}</div>}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {isEditing ? (
                        <div className="space-y-2">
                          <input
                            className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                            value={editForm.status}
                            onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}
                            placeholder="Status text"
                          />
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={editForm.isActive} onChange={e => setEditForm(f => ({ ...f, isActive: e.target.checked }))} />
                            <span className="text-[10px] font-bold uppercase">App Active</span>
                          </label>
                        </div>
                      ) : (
                        <div className="space-y-1 text-center">
                          <span className={`block px-2 py-0.5 text-[10px] font-bold rounded uppercase ${school.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {school.isActive ? 'Active' : 'Inactive'}
                          </span>
                          {school.status && <div className="text-[9px] text-gray-400 italic truncate max-w-[80px]">{school.status}</div>}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {isEditing ? (
                        <div className="flex gap-2">
                          <button
                            className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-3 py-1 rounded text-sm transition-colors disabled:cursor-not-allowed"
                            onClick={() => saveEdit(id)}
                            disabled={savingId === id}
                          >
                            {savingId === id ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            className="border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-1 rounded text-sm transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                            onClick={cancelEdit}
                            disabled={savingId === id}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <button
                            className="text-blue-600 hover:text-blue-800 px-3 py-1 border border-blue-300 hover:bg-blue-50 rounded text-sm transition-colors disabled:text-gray-400 disabled:border-gray-300 disabled:cursor-not-allowed"
                            onClick={() => startEdit(school)}
                            disabled={deletingId === id}
                          >
                            Edit
                          </button>
                          <button
                            className="text-red-600 hover:text-red-800 px-3 py-1 border border-red-300 hover:bg-red-50 rounded text-sm transition-colors disabled:text-gray-400 disabled:border-gray-300 disabled:cursor-not-allowed"
                            onClick={() => removeSchool(id)}
                            disabled={deletingId === id}
                          >
                            {deletingId === id ? 'Deleting…' : 'Delete'}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}

              {schools.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                    No schools yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Schools;
